import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid
} from '@mui/material';

export const CHANGE_REQUEST_TYPE = 'Change Request Form';

export const emptyChangeRequest = () => ({
  requesterFirstName: '',
  requesterLastName: '',
  email: '',
  raisedAt: null,
  proposedChange: '',
  changeReasons: '',
  changeImpact: ''
});

export const formatChangeRequestDescription = (data) => {
  return [
    data.raisedAt ? `Raised At: ${new Date(data.raisedAt).toLocaleString()}` : null,
    `Requester: ${data.requesterFirstName} ${data.requesterLastName}`,
    `Email: ${data.email}`,
    '',
    'Proposed Change:',
    data.proposedChange,
    '',
    'Reasons for Change:',
    data.changeReasons,
    '',
    'Change Impact:',
    data.changeImpact
  ].filter(Boolean).join('\n');
};

export const validateChangeRequest = (data) => {
  if (!data.requesterFirstName?.trim()) return 'Requester first name is required';
  if (!data.requesterLastName?.trim()) return 'Requester last name is required';
  if (!data.email?.trim()) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) return 'Please enter a valid email';
  if (!data.proposedChange?.trim()) return 'Proposed Change is required';
  if (!data.changeReasons?.trim()) return 'Reasons for change are required';
  if (!data.changeImpact?.trim()) return 'Change Impact is required';
  return null;
};

const RequiredLabel = ({ children }) => (
  <Typography component="label" variant="subtitle2" sx={{ fontWeight: 600, color: '#0d47a1', mb: 0.5, display: 'block' }}>
    {children}
    <Box component="span" sx={{ color: 'error.main' }}> *</Box>
  </Typography>
);

const ChangeRequestFormSection = ({ value, onChange, currentUser }) => {
  const onInputChange = (field) => (event) => {
    onChange({ ...value, [field]: event.target.value });
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        background: 'linear-gradient(180deg, #e3f2fd 0%, #bbdefb 100%)',
        color: '#0d47a1'
      }}
    >
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {`Raised At: ${
            value.raisedAt
              ? new Date(value.raisedAt).toLocaleString()
              : new Date().toLocaleString()
          }`}
        </Typography>
        {currentUser && (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {`Raised By: ${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()}
          </Typography>
        )}
      </Box>

      <Typography
        variant="h5"
        align="center"
        sx={{ fontWeight: 700, color: '#0d47a1', mb: 3 }}
      >
        Change Request Form
      </Typography>

      <Box sx={{ mb: 2 }}>
        <RequiredLabel>Change Requester</RequiredLabel>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="First"
              value={value.requesterFirstName}
              onChange={onInputChange('requesterFirstName')}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.9)' } }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Last"
              value={value.requesterLastName}
              onChange={onInputChange('requesterLastName')}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.9)' } }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 2 }}>
        <RequiredLabel>Email</RequiredLabel>
        <TextField
          fullWidth
          size="small"
          type="email"
          value={value.email}
          onChange={onInputChange('email')}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.9)' } }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <RequiredLabel>Proposed Change</RequiredLabel>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={value.proposedChange}
          onChange={onInputChange('proposedChange')}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.9)' } }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <RequiredLabel>Reasons you want the specified details changed</RequiredLabel>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={value.changeReasons}
          onChange={onInputChange('changeReasons')}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.9)' } }}
        />
      </Box>

      <Box>
        <RequiredLabel>Change Impact</RequiredLabel>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={value.changeImpact}
          onChange={onInputChange('changeImpact')}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.9)' } }}
        />
      </Box>
    </Box>
  );
};

export default ChangeRequestFormSection;
